/**
 * Customer Timeline Store.
 *
 * Mirrors eventsStore.ts (Task 2556) parameterized by customerId.
 * Key deltas from the global events store:
 * - customerId is fixed in state — not a user-mutable filter
 * - Default time range: last 90 days (vs 24h for global events)
 * - customerProfile slot for the standalone header strip
 * - No customerId filter facet
 *
 * Live-merge, memory cap, cursor-pagination logic are verbatim from 2556.
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { PlatformEvent, ConnectionState, EventCategory } from '@/features/events/types'
import type { CustomerProfile, CustomerTimelineFilter } from './types'
import { DEFAULT_TIMELINE_DAYS } from './types'
import { timelineTimestamp } from '@/features/events/lib/eventTimestamps'

const MAX_EVENTS = 1000 // Memory cap per STREAM_AESTHETIC.md 7.4

// ── State ─────────────────────────────────────────────────────────────────────

export interface CustomerTimelineState {
  // Fixed customer context
  customerId: string | null
  customerProfile: CustomerProfile | null
  customerProfileLoading: boolean
  customerProfileError: string | null

  // Event data
  events: PlatformEvent[]
  eventIds: Set<string>
  pendingEvents: PlatformEvent[]
  olderEventsCount: number

  // Connection
  connectionState: ConnectionState
  lastConnectedAt: Date | null
  isPaused: boolean

  // Loading
  isLoading: boolean
  isLoadingHistory: boolean
  hasMoreHistory: boolean
  error: string | null
  oldestLoadedTimestamp: number | null

  // Filters (no customerId facet — fixed by route param)
  filters: CustomerTimelineFilter

  // Selection
  selectedEventId: string | null
  expandedEventIds: Set<string>
  detailEventId: string | null
}

// ── Actions ───────────────────────────────────────────────────────────────────

export interface CustomerTimelineActions {
  // Customer context
  setCustomerId: (id: string) => void
  setCustomerProfile: (profile: CustomerProfile | null) => void
  setCustomerProfileLoading: (loading: boolean) => void
  setCustomerProfileError: (error: string | null) => void

  // Event actions
  addLiveEvent: (event: PlatformEvent) => void
  addPendingEvent: (event: PlatformEvent) => void
  flushPendingEvents: () => void
  appendHistoricalEvents: (events: PlatformEvent[], hasMore: boolean) => void
  clearEvents: () => void

  // Connection
  setConnectionState: (state: ConnectionState) => void
  setIsPaused: (paused: boolean) => void
  togglePause: () => void

  // Loading
  setLoading: (loading: boolean) => void
  setLoadingHistory: (loading: boolean) => void
  setError: (error: string | null) => void

  // Filters
  setFilters: (filters: Partial<CustomerTimelineFilter>) => void
  clearFilters: () => void
  setFiltersFromUrl: (params: URLSearchParams) => void

  // Selection
  selectEvent: (id: string | null) => void
  selectNext: () => void
  selectPrevious: () => void
  toggleExpanded: (id: string) => void
  collapseAll: () => void
  openDetail: (id: string) => void
  closeDetail: () => void

  // Computed
  getFilteredEvents: () => PlatformEvent[]
  getSelectedEvent: () => PlatformEvent | null
  getDetailEvent: () => PlatformEvent | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseFiltersFromUrl(params: URLSearchParams): CustomerTimelineFilter {
  const filters: CustomerTimelineFilter = {}

  const q = params.get('q')
  if (q) filters.q = q

  const type = params.get('type')
  if (type) filters.type = type.split(',')

  const source = params.get('source')
  if (source) filters.source = source.split(',')

  const category = params.get('category')
  if (category) filters.category = category.split(',') as EventCategory[]

  const from = params.get('from')
  if (from) filters.from = from

  const to = params.get('to')
  if (to) filters.to = to

  return filters
}

function applyFilters(events: PlatformEvent[], filters: CustomerTimelineFilter): PlatformEvent[] {
  return events.filter((event) => {
    if (filters.q) {
      const q = filters.q.toLowerCase()
      const matchesType = event.type.toLowerCase().includes(q)
      const matchesResource = event.resource_name?.toLowerCase().includes(q)
      if (!matchesType && !matchesResource) return false
    }

    if (filters.type && filters.type.length > 0) {
      if (!filters.type.some((t) => event.type.includes(t))) return false
    }

    if (filters.source && filters.source.length > 0) {
      if (!filters.source.some((s) => event.source?.includes(s))) return false
    }

    if (filters.category && filters.category.length > 0) {
      if (!filters.category.includes(event.category)) return false
    }

    // Time range filter — keyed on occurred_at (the true action time) so
    // historic ingestions are shown in their correct timeline position.
    if (filters.from) {
      const fromTs =
        typeof filters.from === 'string'
          ? new Date(filters.from).getTime() / 1000
          : filters.from
      if (timelineTimestamp(event) < fromTs) return false
    }

    if (filters.to) {
      const toTs =
        typeof filters.to === 'string'
          ? new Date(filters.to).getTime() / 1000
          : filters.to
      if (timelineTimestamp(event) > toTs) return false
    }

    return true
  })
}

// ── Default filters (90d window) ──────────────────────────────────────────────

function getDefaultFilters(): CustomerTimelineFilter {
  const ninetyDaysAgo = Math.floor(
    (Date.now() - DEFAULT_TIMELINE_DAYS * 24 * 60 * 60 * 1000) / 1000
  )
  return { from: ninetyDaysAgo }
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useCustomerTimelineStore = create<
  CustomerTimelineState & CustomerTimelineActions
>()(
  subscribeWithSelector((set, get) => ({
    // ── Initial State ──────────────────────────────────────────────────────────

    customerId: null,
    customerProfile: null,
    customerProfileLoading: false,
    customerProfileError: null,

    events: [],
    eventIds: new Set(),
    pendingEvents: [],
    olderEventsCount: 0,

    connectionState: 'idle',
    lastConnectedAt: null,
    isPaused: false,

    isLoading: false,
    isLoadingHistory: false,
    hasMoreHistory: true,
    error: null,
    oldestLoadedTimestamp: null,

    filters: getDefaultFilters(),

    selectedEventId: null,
    expandedEventIds: new Set(),
    detailEventId: null,

    // ── Customer Context ───────────────────────────────────────────────────────

    setCustomerId: (id) => set({ customerId: id }),

    setCustomerProfile: (profile) => set({ customerProfile: profile }),

    setCustomerProfileLoading: (loading) => set({ customerProfileLoading: loading }),

    setCustomerProfileError: (error) => set({ customerProfileError: error }),

    // ── Event Actions ──────────────────────────────────────────────────────────

    addLiveEvent: (event) => {
      set((state) => {
        if (state.eventIds.has(event.id)) return state

        const newIds = new Set(state.eventIds)
        newIds.add(event.id)

        let newEvents = [event, ...state.events]
        let olderCount = state.olderEventsCount

        if (newEvents.length > MAX_EVENTS) {
          olderCount += newEvents.length - MAX_EVENTS
          newEvents = newEvents.slice(0, MAX_EVENTS)
        }

        return { events: newEvents, eventIds: newIds, olderEventsCount: olderCount }
      })
    },

    addPendingEvent: (event) => {
      set((state) => ({
        pendingEvents: [event, ...state.pendingEvents],
      }))
    },

    flushPendingEvents: () => {
      const { pendingEvents, addLiveEvent } = get()
      for (const event of [...pendingEvents].reverse()) {
        addLiveEvent(event)
      }
      set({ pendingEvents: [] })
    },

    appendHistoricalEvents: (events, hasMore) => {
      // Guard against an undefined or non-array argument — e.g. an upstream
      // API response shape mismatch — so a single bad payload can't crash the
      // whole timeline.
      const safeEvents = Array.isArray(events) ? events : []
      set((state) => {
        const newIds = new Set(state.eventIds)
        const uniqueEvents = safeEvents
          .filter((e) => {
            if (newIds.has(e.id)) return false
            newIds.add(e.id)
            return true
          })
          // Sort by occurred_at (falling back to created) so historic
          // ingestions land in their true timeline position.
          .sort((a, b) => timelineTimestamp(b) - timelineTimestamp(a))

        if (uniqueEvents.length === 0) {
          return { isLoadingHistory: false, hasMoreHistory: hasMore }
        }

        let newEvents = [...state.events, ...uniqueEvents]
        let olderCount = state.olderEventsCount

        if (newEvents.length > MAX_EVENTS) {
          olderCount += newEvents.length - MAX_EVENTS
          newEvents = newEvents.slice(0, MAX_EVENTS)
        }

        const oldestEvent = uniqueEvents[uniqueEvents.length - 1]
        const oldestTimestamp = oldestEvent?.created ?? state.oldestLoadedTimestamp

        return {
          events: newEvents,
          eventIds: newIds,
          oldestLoadedTimestamp: oldestTimestamp,
          hasMoreHistory: hasMore && newEvents.length < MAX_EVENTS,
          isLoadingHistory: false,
          olderEventsCount: olderCount,
        }
      })
    },

    clearEvents: () => {
      set({
        events: [],
        eventIds: new Set(),
        pendingEvents: [],
        olderEventsCount: 0,
        oldestLoadedTimestamp: null,
        hasMoreHistory: true,
      })
    },

    // ── Connection ─────────────────────────────────────────────────────────────

    setConnectionState: (connectionState) => {
      set((state) => ({
        connectionState,
        lastConnectedAt:
          connectionState === 'connected' ? new Date() : state.lastConnectedAt,
      }))
    },

    setIsPaused: (isPaused) => set({ isPaused }),

    togglePause: () => set((state) => ({ isPaused: !state.isPaused })),

    // ── Loading ────────────────────────────────────────────────────────────────

    setLoading: (isLoading) => set({ isLoading }),

    setLoadingHistory: (isLoadingHistory) => set({ isLoadingHistory }),

    setError: (error) => set({ error }),

    // ── Filters ────────────────────────────────────────────────────────────────

    setFilters: (filters) => {
      set((state) => ({ filters: { ...state.filters, ...filters } }))
    },

    clearFilters: () => set({ filters: getDefaultFilters() }),

    setFiltersFromUrl: (params) => {
      const parsed = parseFiltersFromUrl(params)
      // Preserve 90d default if no time range in URL
      if (!parsed.from && !parsed.to) {
        parsed.from = getDefaultFilters().from
      }
      set({ filters: parsed })
    },

    // ── Selection ──────────────────────────────────────────────────────────────

    selectEvent: (id) => set({ selectedEventId: id }),

    selectNext: () => {
      set((state) => {
        const filtered = get().getFilteredEvents()
        if (filtered.length === 0) return state

        const currentIndex = state.selectedEventId
          ? filtered.findIndex((e) => e.id === state.selectedEventId)
          : -1

        const nextIndex = Math.min(currentIndex + 1, filtered.length - 1)
        return { selectedEventId: filtered[nextIndex]?.id ?? null }
      })
    },

    selectPrevious: () => {
      set((state) => {
        const filtered = get().getFilteredEvents()
        if (filtered.length === 0) return state

        const currentIndex = state.selectedEventId
          ? filtered.findIndex((e) => e.id === state.selectedEventId)
          : 0

        const prevIndex = Math.max(currentIndex - 1, 0)
        return { selectedEventId: filtered[prevIndex]?.id ?? null }
      })
    },

    toggleExpanded: (id) => {
      set((state) => {
        const next = new Set(state.expandedEventIds)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return { expandedEventIds: next }
      })
    },

    collapseAll: () => set({ expandedEventIds: new Set() }),

    openDetail: (id) => set({ detailEventId: id }),

    closeDetail: () => set({ detailEventId: null }),

    // ── Computed ───────────────────────────────────────────────────────────────

    getFilteredEvents: () => {
      const { events, filters } = get()
      if (Object.keys(filters).length === 0) return events
      return applyFilters(events, filters)
    },

    getSelectedEvent: () => {
      const { selectedEventId, events } = get()
      if (!selectedEventId) return null
      return events.find((e) => e.id === selectedEventId) ?? null
    },

    getDetailEvent: () => {
      const { detailEventId, events } = get()
      if (!detailEventId) return null
      return events.find((e) => e.id === detailEventId) ?? null
    },
  }))
)
